import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useTranslation } from "react-i18next";

/**
 * VP-101 — presentational, non-interactive showcase in the Stories creator header.
 *
 * Shows CURATED (pinned) real Stories sequences as IG-styled 9:16 frame groups
 * auto-scrolling left in a seamless loop (pauses on hover). The WHOLE sequence is
 * visible — every frame rendered in order with a frame number — not just a single
 * thumbnail. Curation = the existing "Przypnij/Pin" action (super_admin) which
 * sets isInGallery; this reads /sequences/gallery. Renders nothing when nothing
 * is pinned. Pure eye-candy: no clicks, never drives generation or style.
 */

interface ShowcaseFrame {
  index: number;
  url: string;
}
interface ShowcaseSequence {
  id: string;
  title: string | null;
  frameCount: number;
  frames: ShowcaseFrame[];
}
interface GalleryResponse {
  sequences: ShowcaseSequence[];
}

function SequenceGroup({ seq }: { seq: ShowcaseSequence }): JSX.Element {
  const { t } = useTranslation("storiesCreator");
  return (
    <div className="flex-shrink-0 select-none">
      {/* group header: title + "Wygenerowane" tag */}
      <div className="flex items-center gap-1.5 mb-1.5 px-0.5">
        <span className="inline-flex items-center gap-1 bg-[#ef3e29]/90 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">
          <Sparkles size={8} />
          {t("showcase.generatedTag", { defaultValue: "Wygenerowane" })}
        </span>
        <span className="text-white/55 text-[10px] font-medium truncate max-w-[180px]">
          {seq.title ?? t("showcase.untitled", { defaultValue: "Sekwencja Stories" })}
        </span>
      </div>
      {/* the whole sequence: every frame in order, numbered */}
      <div className="flex gap-1.5">
        {seq.frames.map((f, i) => (
          <div
            key={f.index}
            className="relative w-20 sm:w-24 aspect-[9/16] rounded-lg overflow-hidden bg-zinc-900 border border-white/[0.08] shadow-md"
          >
            <img
              src={f.url}
              alt={`${seq.title ?? "Stories"} — ${i + 1}`}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              draggable={false}
            />
            {/* frame number */}
            <div className="absolute top-1 right-1 bg-black/70 text-white text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
              {i + 1}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StoriesShowcaseMarquee(): JSX.Element | null {
  const { data } = useQuery<GalleryResponse>({
    queryKey: ["/api/stories-creator/sequences/showcase"],
    queryFn: () => apiRequest("GET", "/api/stories-creator/sequences/showcase"),
    staleTime: 5 * 60 * 1000,
  });

  const sequences = useMemo(
    () => (data?.sequences ?? []).filter((s) => s.frames && s.frames.length > 0),
    [data]
  );

  // Repeat the (curated, possibly small) set so it fills the viewport, then render
  // the half twice and slide by -50% for a jump-free loop.
  const half = useMemo(() => {
    if (sequences.length === 0) return [];
    const out: ShowcaseSequence[] = [];
    while (out.length < Math.max(4, sequences.length)) out.push(...sequences);
    return out;
  }, [sequences]);

  // Empty until something is pinned to the gallery — by design (curated source).
  if (half.length === 0) return null;

  const totalFrames = half.reduce((sum, s) => sum + s.frames.length, 0);
  const durationSec = Math.max(24, totalFrames * 3);

  return (
    <div className="relative w-full overflow-hidden group" aria-hidden="true">
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-12 z-10 bg-gradient-to-r from-[#0a0c10] to-transparent" />
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 z-10 bg-gradient-to-l from-[#0a0c10] to-transparent" />

      <div
        className="flex gap-6 w-max animate-marquee-left group-hover:[animation-play-state:paused] pointer-events-none py-1"
        style={{ animationDuration: `${durationSec}s` }}
      >
        {half.map((seq, i) => (
          <SequenceGroup key={`a-${i}-${seq.id}`} seq={seq} />
        ))}
        {half.map((seq, i) => (
          <SequenceGroup key={`b-${i}-${seq.id}`} seq={seq} />
        ))}
      </div>
    </div>
  );
}
